import View from "@/components/common/view";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import keys from "lodash/keys";
import qs from "query-string";

import AssistiveFiltersSection from "../assistiveFilters";
import ProductListingFilters from "../productListingFiltersWeb";
import FilteredBy from "../filteredBy";
import useAssistiveFilters from "../hooks/useAssistiveFilters";
import useSelectedByQuery from "../hooks/useSelectedByQuery";
import PlpAnalytics from "@/services/analytics/main/plp";
import { SORT_LOCATION } from "@/services/analytics/main/plp/types";
import { handleFilterEvent } from "@/routes/productListing/analyticsHelper";

import { FILTER_QUERY_PARAMS, FILTER_TYPES } from "../constants";
import { useRouteSlug } from "@/hooks";
import useSelectedFilters from "../hooks/useSelectedFilters";
import {
  AssistiveFilterOption,
  Facet,
  FilterOption,
  SelectedFiltersType
} from "../types";
import {
  buildFacetsForListingUrlMerge,
  buildPlpQueryFromFilterSelection,
  collectCategoryQueryParamKeysFromFacets,
  collectFilterQueryParamKeysFromFacets,
  getTouchedQueryParamsFromSelection,
  mergeSelectedFiltersFromUrlAndListing,
  resolveTagToFilterKey,
  splitSizeFacetInFacets
} from "../utils";

const WebFilteringSection = ({ listingPageData, listingFilters }) => {
  const history = useHistory();
  const location = useLocation();
  const routeSlug = useRouteSlug();
  const selectedByQuery = useSelectedByQuery();
  const assistiveFilter = useAssistiveFilters(listingPageData);
  const selectedFiltersData = useSelectedFilters(listingPageData);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersType>(
    {}
  );

  const listingPageDataRef = useRef(listingPageData);
  listingPageDataRef.current = listingPageData;
  const selectedFiltersDataRef = useRef(selectedFiltersData);
  selectedFiltersDataRef.current = selectedFiltersData;

  const preparedFilters = useMemo(
    () => splitSizeFacetInFacets(listingFilters ?? []),
    [listingFilters]
  );
  const filterQueryParamKeys = useMemo(
    () => collectFilterQueryParamKeysFromFacets(preparedFilters as any),
    [preparedFilters]
  );
  const categoryQueryParamKeys = useMemo(
    () => collectCategoryQueryParamKeysFromFacets(listingFilters ?? []),
    [listingFilters]
  );
  useEffect(() => {
    if (!listingPageDataRef.current) return;

    if (isEmpty(selectedByQuery) || isEqual(keys(selectedByQuery), ["q"])) {
      setSelectedFilters(prev => (isEmpty(prev) ? prev : {}));
      return;
    }

    const facetsFromListingResponse = buildFacetsForListingUrlMerge(
      selectedFiltersDataRef.current,
      selectedByQuery
    );

    setSelectedFilters(previousSelection => {
      const merged = mergeSelectedFiltersFromUrlAndListing(
        previousSelection,
        facetsFromListingResponse,
        selectedByQuery
      );
      return isEqual(previousSelection, merged) ? previousSelection : merged;
    });
  }, [selectedByQuery]);

  const updateSelectedFilters = useCallback(
    (selected, latestSelectedFilters = null) => {
      let merged: SelectedFiltersType = {} as SelectedFiltersType;

      setSelectedFilters(prev => {
        const base: SelectedFiltersType = {
          ...(latestSelectedFilters ?? prev)
        };

        if (selected) {
          if (Array.isArray(selected)) {
            selected.forEach(item => {
              base[item.queryParam] = item;
            });
          } else {
            base[selected.queryParam] = selected;
          }
          merged = { ...prev, ...base };
          return merged;
        }

        merged = {};
        return {};
      });

      return merged;
    },
    []
  );

  const onAssistiveFilterClick = (filter: AssistiveFilterOption) => {
    updateSelectedFilters(null);
    history.push(`/${routeSlug}${filter.url}`);
  };

  const getUpdatedQuery = useCallback(
    (
      updatedSelection: SelectedFiltersType,
      opts?: {
        touchedQueryParamKeys?: Set<string>;
        fullFilterReplace?: boolean;
      }
    ) => {
      return buildPlpQueryFromFilterSelection(
        location.pathname,
        location.search,
        updatedSelection,
        {
          filterQueryParamKeys,
          touchedQueryParamKeys:
            opts?.touchedQueryParamKeys ?? new Set<string>(),
          fullFilterReplace: opts?.fullFilterReplace ?? false
        }
      );
    },
    [location, filterQueryParamKeys]
  );

  const onSelection = useCallback(
    (selected, latestSelectedFilters) => {
      const updatedSelection = updateSelectedFilters(
        selected,
        latestSelectedFilters
      );
      const touched = getTouchedQueryParamsFromSelection(
        selected,
        categoryQueryParamKeys
      );
      const query = getUpdatedQuery(updatedSelection, {
        touchedQueryParamKeys: touched,
        fullFilterReplace: false
      });

      const sortFacet = Array.isArray(selected) ? selected[0] : selected;
      if (sortFacet?.queryParam === FILTER_QUERY_PARAMS.SORT) {
        PlpAnalytics.sort({
          sort_by: sortFacet?.selectedOptions?.[0]?.defaultValue?.toLowerCase(),
          sort_location: SORT_LOCATION.PRODUCT_LIST
        });
      } else {
        handleFilterEvent(selected, updatedSelection);
      }

      history.push(query);
    },
    [updateSelectedFilters, getUpdatedQuery, categoryQueryParamKeys]
  );

  const tags = useMemo(
    () =>
      selectedFiltersData?.flatMap(filterData => {
        if (filterData.type === FILTER_TYPES.PRICE) return [];
        const { data: filterOptions, ...filterOnlyData } = filterData;
        const optionsArray: FilterOption[] = Array.isArray(filterOptions)
          ? filterOptions
          : [];
        return optionsArray.map(option => ({
          filter: { queryParam: option.queryParam, ...filterOnlyData },
          ...option
        }));
      }),
    [selectedFiltersData]
  );

  const onClearAll = useCallback(() => {
    updateSelectedFilters(null);
    const { q } = qs.parse(location.search);
    const url = qs.stringifyUrl(
      { url: location.pathname, query: { q } },
      { skipNull: true, skipEmptyString: true, encode: false }
    );
    history.push(url);
  }, [updateSelectedFilters]);

  const onTagRemove = useCallback(
    tag => {
      const filterKey = resolveTagToFilterKey(tag, selectedFilters);

      if (!filterKey) return;

      const current = selectedFilters[filterKey];
      const currentOptions =
        current?.selectedOptions ??
        (selectedByQuery[filterKey]?.selectedOptions as FilterOption[]);
      if (!currentOptions?.length) return;

      const nextOptions = currentOptions.filter(
        option => option.key !== tag.key
      );
      const newFilterSelection = {
        ...(current ?? {}),
        queryParam: filterKey,
        selectedOptions: nextOptions
      };
      const updatedSelection = updateSelectedFilters(newFilterSelection);
      const touched = new Set<string>();
      if (categoryQueryParamKeys.has(filterKey)) {
        categoryQueryParamKeys.forEach(k => touched.add(k));
      } else {
        touched.add(filterKey);
      }
      const query = getUpdatedQuery(updatedSelection, {
        touchedQueryParamKeys: touched,
        fullFilterReplace: false
      });
      history.push(query);
    },
    [
      updateSelectedFilters,
      getUpdatedQuery,
      selectedFilters,
      selectedByQuery,
      history,
      categoryQueryParamKeys
    ]
  );

  return (
    <View.desktop>
      {assistiveFilter && (
        <AssistiveFiltersSection
          staticFilter={assistiveFilter.prev_selection}
          filters={assistiveFilter.list}
          onFilterClick={onAssistiveFilterClick}
        />
      )}
      <SelectedFiltersContext.Provider
        value={{
          selectedFilters,
          setSelectedFilters,
          updateSelectedFilters,
          onSelection
        }}
      >
        <ProductListingFilters filters={listingFilters} />
        <FilteredBy
          tagList={tags}
          onTagRemove={onTagRemove}
          onClearAll={onClearAll}
        />
      </SelectedFiltersContext.Provider>
    </View.desktop>
  );
};

export const SelectedFiltersContext = createContext<{
  selectedFilters: SelectedFiltersType;
  setSelectedFilters: React.Dispatch<
    React.SetStateAction<{
      [key: string]: Facet & {
        selectedOptions: FilterOption[];
      };
    }>
  >;
  updateSelectedFilters: (selected?: any) => {
    [key: string]: Facet & {
      selectedOptions: FilterOption[];
    };
  };
  onSelection: (...args) => void;
}>(null);

export default WebFilteringSection;
